import * as React from "react";
import styled from "styled-components";
import { ApplicationDto, ApplicationCodes } from "Api/Api";
import { NavLink } from "react-router-dom";
import { postGuidepostLogAppVisitAsync } from "State/Guidepost/GuidepostReducer";
import { StyledFlex } from "Components/Shared/StyledComponents";
import { useApplicationIcon } from "Hooks/Guidepost/useApplicationIcon";
import { useDispatch } from "react-redux";
import { useIsInsideIbis } from "Hooks/Guidepost/useIsInsideIbis";
import { formatISO } from "date-fns";
import { DefaultButtonWithTooltip } from "Components/Shared/Buttons/DefaultButtonWithTooltip";

type Props = {
  application: ApplicationDto;
  logVisit: boolean;
  withName?: boolean;
  showTooltips?: boolean;
};

const StyledApplicationLink = styled(NavLink)`
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  display: block;
  p {
    text-transform: none;
  }
  button {
    min-height: 42px;
  }
  width: 100%;
  padding: ${props => props.theme.spacing(1)}px;
  outline: 0 !important;

  &:focus {
    outline: 0;
  }
`;

export const StyledExternalApplicationLink = styled.a<{
  showTooltips?: boolean;
}>`
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  display: block;
  p {
    text-transform: none;
  }
  button {
    min-height: 42px;
  }
  width: 100%;
  padding: ${props => props.theme.spacing(1)}px;
  outline: 0 !important;

  &:focus {
    outline: 0;
  }

  ${props =>
    !props.showTooltips &&
    `&:hover {
      background-color: ${props.theme.palette.secondary.light};
    }`}
`;

const StyledIcon = styled.div`
  display: flex;
  svg {
    width: 20px;
    height: 20px;
    max-height: 34px;
  }
`;

export const StyledName = styled.div`
  margin-left: ${props => props.theme.spacing(3)}px;
  font-weight: inherit;
`;

const APPLICATION_CODES = [
  ApplicationCodes.OperationalDutyFree,
  ApplicationCodes.BlogicGame,
  ApplicationCodes.GoogleRoot,
  ApplicationCodes.CommitGuru,
  ApplicationCodes.BeTested,
  ApplicationCodes.Jira,
  ApplicationCodes.HelpdeskIT,
  ApplicationCodes.BlogicAIChat,
  ApplicationCodes.LunchBuddy,
  ApplicationCodes.RovoChat,
];

export const GuidepostApplication: React.FunctionComponent<Props> = props => {
  const { application, withName, logVisit, showTooltips } = props;
  const getIcon = useApplicationIcon();
  const dispatch = useDispatch();
  const isInsideIbis = useIsInsideIbis();

  const isExternalApp = (appUrl: string) => {
    return !appUrl.startsWith("/");
  };

  const formatExternalAppUrl = (url: string) => {
    if (!isInsideIbis && url.startsWith("/")) {
      return `${process.env.REACT_APP_IBIS_URL}${url}`;
    }
    return url;
  };

  const logAppVisit = () => {
    logVisit &&
      dispatch(
        postGuidepostLogAppVisitAsync.request({
          applicationCode: application.applicationCode,
          dateCreated: formatISO(new Date()),
        }),
      );
  };

  const body = (showTooltips?: boolean) => (
    <>
      {showTooltips ? (
        <DefaultButtonWithTooltip
          title={withName ? "" : application.name}
          placement={"right"}
        >
          <StyledFlex alignItems={"center"}>
            <StyledIcon>{getIcon(application.applicationCode)}</StyledIcon>
            {withName && <StyledName>{application.name}</StyledName>}
          </StyledFlex>
        </DefaultButtonWithTooltip>
      ) : (
        <StyledFlex alignItems={"center"}>
          <StyledIcon>{getIcon(application.applicationCode)}</StyledIcon>
          {withName && <StyledName>{application.name}</StyledName>}
        </StyledFlex>
      )}
    </>
  );

  const openAppInNewTab = (applicationCode: string) =>
    APPLICATION_CODES.filter(appCode => applicationCode.includes(appCode))
      .length !== 0;

  return (
    <StyledFlex>
      {isExternalApp(application.url) || !isInsideIbis ? (
        <StyledExternalApplicationLink
          href={formatExternalAppUrl(application.url)}
          onMouseUp={logAppVisit}
          target={
            openAppInNewTab(application.applicationCode) ? "_blank" : "_self"
          }
          tabIndex={-1}
          className={"ibis-guidepost-app"}
          showTooltips={showTooltips}
        >
          {body(showTooltips)}
        </StyledExternalApplicationLink>
      ) : (
        <StyledApplicationLink
          to={application.url}
          onMouseUp={logAppVisit}
          tabIndex={-1}
          className={`ibis-guidepost-app ${!showTooltips ? "menu-link" : ""}`}
          activeStyle={{
            fontWeight: "bold",
          }}
        >
          {body(showTooltips)}
        </StyledApplicationLink>
      )}
    </StyledFlex>
  );
};
